import React from "react";
import useReveal from "../../hooks/useReveal";

export const ProblemSection = () => {
  useReveal();
  return (
    <section
      id="problema"
      data-testid="section-problema"
      className="bdk-section"
      style={{ background: "var(--paper)" }}
    >
      <div className="container-bdk grid lg:grid-cols-2 gap-14 items-center">
        <div
          className="bdk-reveal bdk-reveal--left"
          style={{ "--reveal-delay": "0ms" }}
        >
          <p className="kicker">El problema</p>
          <h2 className="font-display text-[38px] sm:text-[48px] md:text-[56px] lg:text-[64px]">
            Tu negocio puede ser excelente,
            <br />
            pero si en internet no queda claro
            <br />
            <span style={{ color: "var(--red)" }}>
              qué haces, qué vendes y cómo contactarte, pierdes oportunidades.
            </span>
          </h2>
          <p className="mt-5 text-[#666873] text-[15px] md:text-[16px]">
            Depender solo de Facebook, Instagram o WhatsApp deja tu
            información dispersa y sin un lugar propio para presentar tu
            negocio.
          </p>
          <p className="mt-3 text-[#666873] text-[15px] md:text-[16px]">
            Sin una página clara, muchos clientes te preguntan lo mismo una y
            otra vez, y se te complica cotizar, reservar o vender.
          </p>
        </div>

        <div
          className="grid gap-3.5 bdk-reveal bdk-reveal--right"
          style={{ "--reveal-delay": "0ms" }}
        >
          {[
            {
              n: "01",
              t: "Información dispersa",
              d: "Tus servicios, precios y contacto quedan repartidos entre redes sociales, sin un lugar central.",
            },
            {
              n: "02",
              t: "Imagen menos profesional",
              d: "Sin una página propia, cuesta más transmitir seriedad frente a otros negocios de tu giro.",
            },
            {
              n: "03",
              t: "Dificultad para cotizar o vender",
              d: "Sin formularios ni catálogo, cada cotización, cita o pedido depende de un mensaje directo.",
            },
          ].map((c, i) => (
            <article
              key={c.n}
              data-testid={`problema-card-${i}`}
              className="rounded-[10px] p-6"
              style={{
                border: "1px solid var(--line)",
                borderLeft: "4px solid var(--red)",
                background: "#fff",
                boxShadow: "0 10px 30px rgba(17,18,24,.05)",
              }}
            >
              <span className="font-display text-[28px] font-black text-[#d90020]">
                {c.n}
              </span>
              <h3 className="font-display text-[22px] mt-1">{c.t}</h3>
              <p className="mt-1 text-[#666873] text-[13px]">{c.d}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export const SolutionSection = () => {
  useReveal();
  return (
    <section
      id="solucion"
      data-testid="section-solucion"
      className="bdk-section bdk-split-bg"
    >
      <div className="container-bdk">
        <div className="max-w-[820px] mb-14 bdk-reveal bdk-reveal--up">
          <p className="kicker">La solución</p>
          <h2 className="font-display text-[38px] sm:text-[48px] md:text-[56px] lg:text-[64px]">
            Una página web que haga
            <br />
            más que verse bonita.
          </h2>
          <p className="mt-5 text-[#666873] text-[15px] md:text-[16px] max-w-[720px]">
            Diseñamos cada página alrededor del objetivo de tu negocio: que
            explique lo que vendes, genere confianza y facilite que te
            contacten, coticen, reserven o compren.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              tag: "PRO",
              t: "Presentación profesional",
              d: "Para que tu negocio genere confianza desde el primer vistazo.",
            },
            {
              tag: "INFO",
              t: "Información clara",
              d: "Servicios, precios de referencia y datos de contacto sin vueltas.",
            },
            {
              tag: "WA",
              t: "Contacto directo",
              d: "Botones de WhatsApp y formulario para captar oportunidades.",
            },
            {
              tag: "RESP",
              t: "Diseño responsive",
              d: "Se ve bien en celular, tablet y computadora, sin excepciones.",
            },
            {
              tag: "SEO",
              t: "SEO técnico básico",
              d: "Estructura, metadatos y velocidad pensados para que te encuentren.",
            },
            {
              tag: "FORM",
              t: "Formularios",
              d: "Para recibir solicitudes de cotización directamente en tu correo.",
            },
            {
              tag: "PLUS",
              t: "Funciones adicionales",
              d: "Catálogo, agenda, pagos u otras funciones según lo necesite tu negocio.",
            },
            {
              tag: "ESC",
              t: "Escalabilidad",
              d: "Tu página puede crecer conforme crecen las necesidades de tu negocio.",
            },
          ].map((c, i) => (
            <article
              key={c.t}
              data-testid={`solucion-card-${i}`}
              className="p-7 rounded-[12px] bg-white bdk-reveal bdk-reveal--up transition-all"
              style={{
                border: "1px solid var(--line)",
                "--reveal-delay": `${i * 70}ms`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.borderColor = "rgba(217,0,32,.4)";
                e.currentTarget.style.boxShadow =
                  "0 18px 40px rgba(17,18,24,.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "";
                e.currentTarget.style.borderColor = "var(--line)";
                e.currentTarget.style.boxShadow = "";
              }}
            >
              <div
                className="grid place-items-center w-[52px] h-[52px] mb-5 rounded-[10px] text-white font-display font-black text-[16px]"
                style={{ background: "var(--red)" }}
              >
                {c.tag}
              </div>
              <h3 className="font-display text-[25px] leading-none">{c.t}</h3>
              <p className="mt-3 text-[#666873] text-[13px]">{c.d}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
